import * as React from 'react';
import styled from 'styled-components';
import {useRoute} from 'react-router5';
import InputSearch from 'components/InputSearch';
import {InputProps, InputVariant} from 'components/Input';

export interface HeaderSearchProps {
  param?: string;
  renderBefore?: JSX.Element;
  renderAfter?: JSX.Element;
  inputSearch?: Omit<InputProps,
    | 'value'
    | 'onChange'>;
}

const Container = styled.header`
  align-items: center;
  display: flex;
`;

const BeforeContent = styled.div`
  margin-right: 10px;
  flex-shrink: 0;
`;

const AfterContent = styled.div`
  margin-left: 10px;
  flex-shrink: 0;
`;

/**
 * This component renders a search header.
 * @param {HeaderSearchProps} props
 * @return {React.ReactElement<HeaderSearchProps>}
 * @constructor
 */
export default function HeaderSearch(
    props: HeaderSearchProps,
): React.ReactElement<HeaderSearchProps> {
  const {route, router} = useRoute();
  const param = props.param || 'search';

  /**
   * This function renders the content before search.
   * @return {JSX.Element | undefined}
   */
  function renderBefore(): JSX.Element | undefined {
    return props.renderBefore ? (
      <BeforeContent>
        {props.renderBefore}
      </BeforeContent>
    ) : undefined;
  }

  /**
   * This function will render the search input.
   * @return {JSX.Element}
   */
  function renderSearch(): JSX.Element {
    /**
     * This function is a handler for changing the value of the search input.
     * @param {React.ChangeEvent<HTMLInputElement>} event
     */
    function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
      router.navigate(route.name, {
        ...route.params,
        [param]: event.target.value,
      });
    }

    return (
      <InputSearch
        {...props.inputSearch}
        value={route.params[param]}
        variant={InputVariant.Accent}
        onChange={handleChange}
      />
    );
  }

  /**
   * This function renders the content after search.
   * @return {JSX.Element | undefined}
   */
  function renderAfter(): JSX.Element | undefined {
    return props.renderAfter ? (
      <AfterContent>
        {props.renderAfter}
      </AfterContent>
    ) : undefined;
  }

  return (
    <Container>
      {renderBefore()}
      {renderSearch()}
      {renderAfter()}
    </Container>
  );
}
